import React from "react";
import { defineMessages, FormattedMessage, injectIntl, intlShape } from "react-intl";
import PropTypes from "prop-types";

import ProfileDetailsBlock from "../account/profile/ProfileDetailsBlock";
import logo from "../../images/my-yoast-academy-logo.svg";
import { Redirect } from "react-router";
import ErrorDisplay from "../../errors/ErrorDisplay";
import { Button, Title } from "@yoast/ui-library";
import * as styles from "./ProfileDetailsStyles.module.scss";
import { ApiError } from "../../api/customFetch.ts";

const messages = defineMessages( {
	header: {
		id: "enterDetails.header",
		defaultMessage: "Enter your details",
	},
	description: {
		id: "profileDetails.message",
		defaultMessage: "Your MyYoast account has been activated! Fill out this last bit " +
			"of information and you can get started. Don't worry, you can always change these later!",
	},
	goToMyYoast: {
		id: "profileDetails.goToMyYoast",
		defaultMessage: "Continue to MyYoast",
	},
	savingProfile: {
		id: "profileDetails.savingProfile",
		defaultMessage: "Saving your profile...",
	},
	// Friendly, user-facing copy for save failures. The raw API error is for diagnostics
	// only; we never show it. Errors are classified by HTTP status into one of these.
	errorInvalidInput: {
		id: "profileDetails.error.invalidInput",
		defaultMessage: "The information you provided appears to be invalid. Please check it and try again.",
	},
	errorTooManyRequests: {
		id: "profileDetails.error.tooManyRequests",
		defaultMessage: "You've tried this a few times in a row. Please wait a moment and try again.",
	},
	errorGeneric: {
		id: "profileDetails.error.generic",
		defaultMessage: "Something went wrong while saving your profile. Please try again. " +
			"If this keeps happening, [customer_support_link]",
	},
	errorLoadFailed: {
		id: "profileDetails.error.loadFailed",
		defaultMessage: "We couldn't load your details. Please refresh the page to try again. " +
			"If this keeps happening, [customer_support_link]",
	},
} );

/**
 * Maps a save error to the friendly, localized message we want to show the user, keyed on
 * HTTP status. The error body itself is never shown — it is diagnostic only.
 *
 * @param {unknown} error The error thrown by the save mutation (typically an ApiError).
 *
 * @returns {object} The `react-intl` message descriptor to display.
 */
function messageForSaveError( error ) {
	switch ( error?.response?.status ) {
		case 400:
		case 422:
			return messages.errorInvalidInput;
		case 429:
			return messages.errorTooManyRequests;
		default:
			return messages.errorGeneric;
	}
}

/**
 * Component in the first-login flow, where the user enters their first and last name.
 *
 * @param {object} props The props.
 *
 * @returns {React.JSX.Element} The rendered component.
 */
function ProfileDetails( {
	intl,
	attemptSubmitProfile,
	savingProfile = false,
	profileSaved = false,
	profile = { email: "", userFirstName: "", userLastName: "" },
	saveError = null,
	loadError = null,
} ) {
	if ( profileSaved && ! saveError ) {
		return <Redirect to={ "/" } />;
	}

	const displayError = saveError
		? {
			message: intl.formatMessage( messageForSaveError( saveError ) ),
			type: "error",
		}
		: null;

	return <div className={ styles.firstColumn }>
		<img className={ styles.asideLogo } src={ logo } alt="MyYoast - Yoast Academy" />
		<Title size="2">
			<FormattedMessage { ...messages.header } />
		</Title>
		<p>
			<FormattedMessage { ...messages.description } />
		</p>
		{ /* When the profile failed to load there is nothing to seed the form with, so show
			the error in place of the form rather than an empty, misleading set of inputs. */ }
		{ loadError
			? <ErrorDisplay error={ { message: intl.formatMessage( messages.errorLoadFailed ), type: "error" } } />
			: <>
				<ErrorDisplay error={ displayError } />
				<ProfileDetailsBlock
					onSubmit={ attemptSubmitProfile }
					userFirstName={ profile.userFirstName }
					userLastName={ profile.userLastName }
				>
					<Button type="submit" isLoading={ savingProfile } disabled={ savingProfile }>
						<FormattedMessage { ...messages.goToMyYoast } />
					</Button>
				</ProfileDetailsBlock>
			</>
		}
	</div>;
}

export default injectIntl( ProfileDetails );

ProfileDetails.propTypes = {
	intl: intlShape.isRequired,
	attemptSubmitProfile: PropTypes.func.isRequired,
	savingProfile: PropTypes.bool,
	profileSaved: PropTypes.bool,
	profile: PropTypes.object,
	saveError: PropTypes.instanceOf( ApiError ),
	loadError: PropTypes.instanceOf( ApiError ),
};
